import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  computeTriangleVertices,
  screenToBarycentric,
  barycentricToScreen,
  clampToSimplex,
  baryToPercentages,
  barycentricColorBlend,
  type Point2D,
  type BarycentricCoord,
  type TriangleVertices,
} from '../../../../utils/signification.math';
import { AUDIO } from '../../../../utils/audioFeedback';
import './GuidedPractice.css';

interface GuidedPracticeProps {
  onDone: () => void;
}

const PADDING = 40;
const PRACTICE_LABELS = ['Apples', 'Oranges', 'Bananas'];
const PRACTICE_COLORS: [number, number, number][] = [
  [194, 172, 142], // sand/clay — neutral warm
  [139, 158, 183], // slate blue — neutral cool
  [168, 180, 160], // sage/moss — neutral earthy
];

export const GuidedPractice: React.FC<GuidedPracticeProps> = ({ onDone }) => {
  const [weights, setWeights] = useState<BarycentricCoord | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bgCanvasRef = useRef<HTMLCanvasElement>(null);
  const fgCanvasRef = useRef<HTMLCanvasElement>(null);
  const verticesRef = useRef<TriangleVertices | null>(null);
  const sizeRef = useRef({ w: 0, h: 0 });
  const isDragging = useRef(false);

  const renderBackground = useCallback(() => {
    const canvas = bgCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = rect.width;
    const h = rect.height;
    sizeRef.current = { w, h };

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const vertices = computeTriangleVertices(w, h, PADDING);
    verticesRef.current = vertices;

    ctx.clearRect(0, 0, w, h);

    // Color field
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(vertices.A.x, vertices.A.y);
    ctx.lineTo(vertices.B.x, vertices.B.y);
    ctx.lineTo(vertices.C.x, vertices.C.y);
    ctx.closePath();
    ctx.clip();

    const step = 6;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const bary = screenToBarycentric({ x, y }, vertices);
        if (bary.a < -0.01 || bary.b < -0.01 || bary.c < -0.01) continue;
        const clamped = clampToSimplex(bary);
        const color = barycentricColorBlend(clamped, PRACTICE_COLORS[0], PRACTICE_COLORS[1], PRACTICE_COLORS[2]);
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.1;
        ctx.fillRect(x, y, step, step);
      }
    }
    ctx.restore();

    // Outline
    ctx.beginPath();
    ctx.moveTo(vertices.A.x, vertices.A.y);
    ctx.lineTo(vertices.B.x, vertices.B.y);
    ctx.lineTo(vertices.C.x, vertices.C.y);
    ctx.closePath();
    ctx.strokeStyle = 'rgba(26, 24, 20, 0.12)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Setup foreground
    const fgCanvas = fgCanvasRef.current;
    if (fgCanvas) {
      fgCanvas.width = w * dpr;
      fgCanvas.height = h * dpr;
      fgCanvas.style.width = `${w}px`;
      fgCanvas.style.height = `${h}px`;
    }
  }, []);

  useEffect(() => {
    renderBackground();
    const observer = new ResizeObserver(() => renderBackground());
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [renderBackground]);

  const renderForeground = useCallback((bary: BarycentricCoord) => {
    const canvas = fgCanvasRef.current;
    const vertices = verticesRef.current;
    if (!canvas || !vertices) return;

    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, sizeRef.current.w, sizeRef.current.h);

    const screen = barycentricToScreen(bary, vertices);
    const color = barycentricColorBlend(bary, PRACTICE_COLORS[0], PRACTICE_COLORS[1], PRACTICE_COLORS[2]);

    ctx.beginPath();
    ctx.arc(screen.x, screen.y, 16, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.3;
    ctx.fill();
    ctx.globalAlpha = 1;

    ctx.beginPath();
    ctx.arc(screen.x, screen.y, 8, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, []);

  const processPointer = useCallback((clientX: number, clientY: number) => {
    const container = containerRef.current;
    const vertices = verticesRef.current;
    if (!container || !vertices) return;

    const rect = container.getBoundingClientRect();
    const point: Point2D = { x: clientX - rect.left, y: clientY - rect.top };
    const bary = clampToSimplex(screenToBarycentric(point, vertices));
    setWeights(bary);
    renderForeground(bary);
  }, [renderForeground]);

  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    processPointer(e.clientX, e.clientY);
    AUDIO.tap();
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    processPointer(e.clientX, e.clientY);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
  };

  const pcts = weights ? baryToPercentages(weights) : null;

  return (
    <div className="gp-container">
      <div className="gp-header">
        <div className="gp-title">Try it out</div>
        <div className="gp-instruction">
          Tap and drag inside the triangle to place yourself between the three ideas.
          The closer you are to a corner, the stronger that idea feels.
        </div>
      </div>

      <div className="gp-canvas-area">
        <div
          ref={containerRef}
          className="gp-canvas-wrap"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{ touchAction: 'none' }}
        >
          <canvas ref={bgCanvasRef} className="gp-canvas gp-canvas--bg" />
          <canvas ref={fgCanvasRef} className="gp-canvas gp-canvas--fg" />

          {/* Practice vertex labels */}
          {verticesRef.current && (
            <>
              <div className="gp-label gp-label--a" style={{ left: verticesRef.current.A.x, top: verticesRef.current.A.y - 20 }}>
                {PRACTICE_LABELS[0]} {pcts ? `${pcts[0]}%` : ''}
              </div>
              <div className="gp-label gp-label--b" style={{ left: verticesRef.current.B.x - 10, top: verticesRef.current.B.y + 16 }}>
                {PRACTICE_LABELS[1]} {pcts ? `${pcts[1]}%` : ''}
              </div>
              <div className="gp-label gp-label--c" style={{ left: verticesRef.current.C.x + 10, top: verticesRef.current.C.y + 16 }}>
                {PRACTICE_LABELS[2]} {pcts ? `${pcts[2]}%` : ''}
              </div>
            </>
          )}
        </div>
      </div>

      {!weights && (
        <div className="gp-hint">Tap anywhere inside the triangle</div>
      )}

      <button className="gp-done-btn" onClick={onDone}>
        Got it, let's start
      </button>
    </div>
  );
};
